import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParameterDto, SampleFormat } from '@src/types/types';
import { getNumericValue } from '@utils/parameters';
import { BadgeList } from './BadgeList';
import { ParameterBar } from './ParameterBar';
import { ParameterValue } from './ParameterValue';
import { NotDetectable } from './NotDetectable';
import { NotAnalyzable } from './NotAnalyzable';

export const ParameterListItemMiddle = ({
  parameter,
}: {
  parameter: ParameterDto;
}) => {
  const { t } = useTranslation('component', { keyPrefix: 'parameterList' });
  const { value, nonDetectable, notAnalyzable, format, regulations, unit } =
    parameter;
  const showBar =
    format === SampleFormat.NUMERIC &&
    !regulations.some((regulation) => regulation.lowerLimit);
  const { badges, activeBadgeIndex } = useMemo(() => {
    const numericValue = getNumericValue(parameter);
    const isSectioned = regulations?.some(
      (regulation) => regulation.lowerLimit,
    );
    if (isSectioned) {
      return {
        badges: regulations?.map((regulation) => regulation.name!),
        activeBadgeIndex: regulations.findIndex(
          (regulation) =>
            (!regulation.lowerLimit || numericValue >= regulation.lowerLimit) &&
            (!regulation.limit || numericValue < regulation.limit),
        ),
      };
    }
    return { badges: [], activeBadgeIndex: -1 };
  }, [parameter, regulations]);
  return (
    <div
      className="hidden xl:flex print:flex items-center gap-8 flex-1"
      data-testid="parameter-list-item-middle"
    >
      <div className="flex items-center gap-12 w-full">
        <div className="min-w-[80px] flex-[0_0_15%]">
          {nonDetectable ? (
            <NotDetectable rawValue={`${value} ${unit}`} />
          ) : notAnalyzable ? (
            <NotAnalyzable />
          ) : (
            <ParameterValue value={`${value} ${unit}`} />
          )}
        </div>
        {showBar && !nonDetectable && !notAnalyzable && (
          <div className="flex-1">
            <ParameterBar parameter={parameter} expanded={false} />
          </div>
        )}
        {nonDetectable && (
          <div className="px-4 py-1 rounded-full text-sm text-nowrap bg-green text-white">
            {t('notDetectableLong')}
          </div>
        )}
        {notAnalyzable && (
          <div className="px-4 py-1 rounded-full text-sm text-nowrap bg-dark text-white">
            {t('notAnalyzableLong')}
          </div>
        )}
      </div>
      {badges?.length && !nonDetectable && !notAnalyzable ? (
        <BadgeList items={badges} activeIndex={activeBadgeIndex} />
      ) : undefined}
    </div>
  );
};
